import React, { useEffect, useState } from 'react';
import {
  Row, Col, Card, Typography, Form, Input, Button, Divider, List, Space, message, Collapse, theme
} from 'antd';

import { useNavigate, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import useIsMobile from '@/hooks/useIsMobile';
import { useDispatch } from 'react-redux'; // เอา useSelector ออกถ้าไม่ได้ใช้
import BookingStateHook from '@/hooks/useBookingState';
import { createBooking, setGuestInfo, bookingClear, applyCoupon } from '@/AppRedux/actions/booking';
import { setAmount, setDescription } from '@/AppRedux/actions/paymentGateway/paymentBeam';
import { CartItem } from '@/AppRedux/actions/booking/bookingType';
import ErrorModal from '@/modules/booking/components/Modals/ErrorModal';

const GuestInfo: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cart, couponCode, discountAmount, search, error, loading, bookingRef } = BookingStateHook()
  const intl = useIntl();
  const { propcode } = useParams<{ propcode: string }>();
  const isMobile = useIsMobile(992);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

  const [form] = Form.useForm();
  // const [mobileSummaryOpen, setMobileSummaryOpen] = useState(false); // ไม่ได้ใช้
  const { token } = theme.useToken();

  useEffect(() => {
    if (bookingRef && propcode) {
      navigate(`/booking/${propcode}/confirmation`, { replace: true });
    }
  }, [bookingRef, propcode, navigate]);

  useEffect(() => {
    if (error) {
      setIsErrorModalOpen(true);
    }
  }, [error]);

  if (cart.length === 0) {
    return <Typography.Paragraph>{intl.formatMessage({ id: 'booking.noRoomSelected' })}</Typography.Paragraph>;
  }

  const handleCloseErrorModal = () => {
    setIsErrorModalOpen(false);
    dispatch(bookingClear());
  };

  const onFinish = (values: any) => {
    if (!propcode) return;
    dispatch(setGuestInfo(values));
    dispatch(setAmount(cart.reduce((sum, i) => sum + i.subtotal, 0)))
    dispatch(setDescription(`Booking forby ${values.firstName} ${values.lastName}`));
    dispatch(createBooking(values));
  };

  const onApplyCoupon = (v: { coupon: string }) => {
    if (!v.coupon) return;
    dispatch(applyCoupon(v.coupon));
    message.success('Applied coupon');
  };

  const total = cart.reduce((sum, i) => sum + i.subtotal, 0);
  const grand = Math.max(0, total - (discountAmount || 0));

  const onBackToAvaiablePage = () => {
    const params: any = {
      start_date: search.checkIn,
      end_date: search.checkOut,
      adults: search.adults,
      children: search.children,
      infants: search.infants,
    }

    const queryString = new URLSearchParams(params as Record<string, string>).toString()
    navigate(`/booking/${propcode}/availability?${queryString}`)
  }

  // ✨ ฟังก์ชัน Render รายการห้องพัก (ใช้ร่วมกันทั้ง Mobile/Desktop)
  const renderSummaryItem = (it: CartItem) => (
    <List.Item key={it.cartKey} style={{ padding: '16px 0', borderBottom: '1px solid #f0f0f0' }}>
      <div style={{ width: '100%' }}>
        <Row wrap={false} gutter={16} align="top">
          {/* ส่วนข้อมูลห้อง (ซ้าย) */}
          <Col flex="auto">
            <Typography.Text strong style={{ fontSize: '15px', display: 'block', marginBottom: 4 }}>
              {it.room.roomtype_name} <span style={{ fontWeight: 'normal', color: token.colorTextSecondary }}>× {it.qty}</span>
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: '13px', display: 'block', marginBottom: 2 }}>
              {it.rate.rateplan_name}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
              {it.nights} {intl.formatMessage({ id: 'booking.nights' })}
            </Typography.Text>
          </Col>

          {/* ส่วนราคา (ขวา) - ไม่มีปุ่ม Control */}
          <Col flex="none" style={{ textAlign: 'right' }}>
            <Typography.Text strong style={{ fontSize: '16px', color: token.colorPrimary }}>
              ฿ {it.subtotal.toLocaleString()}
            </Typography.Text>
          </Col>
        </Row>
      </div>
    </List.Item>
  );

  return (
    <>
      {isMobile ? (
        <div style={{ paddingBottom: 120 }}>
          {/* Guest form */}
          <Card
            title={intl.formatMessage({ id: 'booking.requiredInfo', defaultMessage: 'Guest information' })}
            style={{ borderRadius: 16 }}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              requiredMark
            >
              <Row gutter={12}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="firstName"
                    label={intl.formatMessage({ id: 'field.firstName' })}
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="lastName"
                    label={intl.formatMessage({ id: 'field.lastName' })}
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="email"
                    label={intl.formatMessage({ id: 'field.email' })}
                    rules={[{ type: 'email', required: true }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="phone"
                    label={intl.formatMessage({ id: 'field.phone' })}
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Divider>
                {intl.formatMessage({
                  id: 'booking.optional',
                  defaultMessage: 'Optional information',
                })}
              </Divider>

              <Row gutter={12}>
                <Col xs={24} md={24}>
                  <Form.Item
                    name="address1"
                    label={intl.formatMessage({ id: 'field.address1' })}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={24}>
                  <Form.Item
                    name="address2"
                    label={intl.formatMessage({ id: 'field.address2' })}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={24}>
                  <Form.Item
                    name="remark"
                    label={intl.formatMessage({ id: 'field.remark' })}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>

          {/* 🔻 Sticky summary: align with Availability mobile pattern */}
          <div
            className="bottom-summary"
            style={{
              background: token.colorBgElevated,
              borderTop: `1px solid ${token.colorBorderSecondary}`,
              boxShadow: (token as any).boxShadowTertiary || token.boxShadow,
              color: token.colorText,
            }}
          >
            <div className="bottom-summary__bar">
              <div className="bottom-summary__total" style={{ color: token.colorText }}>
                {intl.formatMessage({ id: 'booking.total' })}: ฿ {grand.toLocaleString()}
              </div>
              <Space>
                <Button type="default" size="large" onClick={onBackToAvaiablePage}>
                  {intl.formatMessage({ id: 'label.back' })}
                </Button>
                <Button type="primary" size="large" onClick={() => form.submit()} loading={loading}>
                  {intl.formatMessage({ id: 'booking.continue', defaultMessage: 'Continue' })}
                </Button>
              </Space>
            </div>
            <div className="bottom-summary__collapse">
              <Collapse
                ghost
                items={[{
                  key: 'cart',
                  label: intl.formatMessage({ id: 'booking.details', defaultMessage: 'Details' }),
                  children: (
                    <>
                      <List
                        dataSource={cart}
                        split={false} // ปิดเส้นคั่น default เพราะเราจัดการเองใน renderSummaryItem
                        renderItem={renderSummaryItem}
                      />
                      {/* คั่นระหว่างรายการกับส่วนคูปอง/สรุปราคา 
                      <Divider style={{ margin: '12px 0' }} />
                      <Form layout="inline" onFinish={onApplyCoupon} style={{ marginBottom: 12 }}>
                        <Form.Item name="coupon" style={{ flex: 1, marginBottom: 0 }} initialValue={couponCode || ''}>
                          <Input size="middle" placeholder={intl.formatMessage({ id: 'booking.coupon', defaultMessage: 'Coupon code' })} />
                        </Form.Item>
                        <Form.Item style={{ marginBottom: 0, marginRight: 0 }}>
                          <Button htmlType="submit" size="middle">
                            {intl.formatMessage({ id: 'booking.apply', defaultMessage: 'Apply' })}
                          </Button>
                        </Form.Item>
                        {couponCode && (
                          <Button type="link" size="small" onClick={() => console.log('Dispatch reset coupon')} style={{ paddingLeft: 8 }}>
                            reset
                          </Button>
                        )}
                      </Form>
     
                      <Row justify="space-between" style={{ marginBottom: 4 }}>
                        <Typography.Text>{intl.formatMessage({ id: 'booking.total', defaultMessage: 'Total' })}:</Typography.Text>
                        <Typography.Text>฿ {total.toLocaleString()}</Typography.Text>
                      </Row>
                      <Row justify="space-between" style={{ marginBottom: 8 }}>
                        <Typography.Text>{intl.formatMessage({ id: 'booking.discount', defaultMessage: 'Discount' })}:</Typography.Text>
                        <Typography.Text type="danger">- ฿ {discountAmount.toLocaleString()}</Typography.Text>
                      </Row>
                      */}
                      <Row justify="space-between">
                        <Typography.Text strong style={{ fontSize: '16px' }}>{intl.formatMessage({ id: 'booking.grandTotal', defaultMessage: 'Grand total' })}:</Typography.Text>
                        <Typography.Text strong style={{ fontSize: '16px', color: token.colorPrimary }}>฿ {grand.toLocaleString()}</Typography.Text>
                      </Row>

                    </>
                  )
                }]}
              />
            </div>
          </div>

        </div>
      ) : (
        <Row gutter={[16, 16]}>
          <Col xs={24} md={14}>
            <Card
              title={intl.formatMessage({ id: 'booking.requiredInfo' })}
              style={{ borderRadius: 16 }}
            >
              <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                requiredMark
              >
                <Row gutter={12}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="firstName"
                      label={intl.formatMessage({ id: 'field.firstName' })}
                      rules={[{ required: true }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="lastName"
                      label={intl.formatMessage({ id: 'field.lastName' })}
                      rules={[{ required: true }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={12}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="email"
                      label={intl.formatMessage({ id: 'field.email' })}
                      rules={[{ type: 'email', required: true }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="phone"
                      label={intl.formatMessage({ id: 'field.phone' })}
                      rules={[{ required: true }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                <Divider>
                  {intl.formatMessage({ id: 'booking.optional' })}
                </Divider>
                <Row gutter={12}>
                  <Col xs={24} md={24}>
                    <Form.Item
                      name="address1"
                      label={intl.formatMessage({ id: 'field.address1' })}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={24}>
                    <Form.Item
                      name="address2"
                      label={intl.formatMessage({ id: 'field.address2' })}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={24}>
                    <Form.Item
                      name="remark"
                      label={intl.formatMessage({ id: 'field.remark' })}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Button
                      type="default"
                      block
                      size="large"
                      onClick={onBackToAvaiablePage}
                    >
                      {intl.formatMessage({ id: 'label.back' })}
                    </Button>
                  </Col>
                  <Col span={12}>
                    <Button
                      type="primary"
                      htmlType="submit"
                      block
                      size="large"
                      loading={loading}
                    >
                      {intl.formatMessage({ id: 'booking.continue' })}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card>
          </Col>

          {/* Summary Card */}
          <Col xs={24} md={10}>
            <Space orientation="vertical" style={{ width: '100%' }}>
              <Card
                title={intl.formatMessage({ id: 'booking.summary' })}
                style={{ borderRadius: 16 }}
              >
                <List
                  dataSource={cart}
                  split={false}
                  renderItem={renderSummaryItem}
                />
                <Divider style={{ margin: '16px 0' }} />
                {/*
                <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
                  <Typography.Text>{intl.formatMessage({ id: 'booking.total' })}:</Typography.Text>
                  <Typography.Text>฿ {total.toLocaleString()}</Typography.Text>
                </Row>

                <Form
                  layout="inline"
                  onFinish={onApplyCoupon}
                  style={{ marginBottom: 8 }}
                >
                  <Form.Item
                    name="coupon"
                    style={{ flex: 1, marginRight: 8 }}
                    initialValue={couponCode || ''}
                  >
                    <Input
                      placeholder={intl.formatMessage({
                        id: 'booking.coupon',
                      })}
                    />
                  </Form.Item>
                  <Form.Item style={{ marginRight: 0 }}>
                    <Button htmlType="submit">
                      {intl.formatMessage({
                        id: 'booking.apply',
                      })}
                    </Button>
                  </Form.Item>
                  {couponCode && (
                    <Button
                      type="link"
                      onClick={() => console.log("Dispatch reset coupon")}
                    >
                      reset
                    </Button>
                  )}
                </Form>

                <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                  <Typography.Text>{intl.formatMessage({ id: 'booking.discount' })}:</Typography.Text>
                  <Typography.Text type="danger">- ฿ {discountAmount.toLocaleString()}</Typography.Text>
                </Row>
                <Divider style={{ margin: '12px 0' }} />
                  */}
                <Row justify="space-between" align="middle">
                  <Typography.Title level={5} style={{ margin: 0 }}>
                    {intl.formatMessage({ id: 'booking.grandTotal' })}
                  </Typography.Title>
                  <Typography.Title level={4} style={{ margin: 0, color: token.colorPrimary }}>
                    ฿ {grand.toLocaleString()}
                  </Typography.Title>
                </Row>
              </Card>
            </Space>
          </Col>
        </Row>
      )}
      <ErrorModal
        isOpen={isErrorModalOpen}
        error={error}
        onClose={handleCloseErrorModal}
      />
    </>
  );
}

export default GuestInfo;